// Shared flatpickr initialisation — the single date-picker implementation reused by the
// admin bundle and every frontend shop bundle. It replaces the three legacy pickers that
// used to coexist (jQuery UI datepicker in admin, bootstrap-datepicker in the frontend,
// and bootstrap-daterangepicker), consolidating everything onto flatpickr.
//
// Field classes:
//   .datepicker            → date only       (d.m.Y)         — frontend + admin date fields
//   .js-flatpickr-datetime → date + time     (d.m.Y H:i)     — admin maintenance window
//   .js-flatpickr-range    → date range      (d.m.Y - d.m.Y) — document archive search
//
// Plus window.initCollectionCalendar(selector, events) — an INLINE event calendar for the
// collection "calendar" view (view type 2, module/collection/show_datepicker.inc.php),
// replacing the old jQuery-UI inline datepicker.
//
// Declarative per-field data-* attributes (so the PHP markup stays the source of truth):
//   data-language      'de' | 'en'   picker locale (German unless explicitly 'en')
//   data-date-end-date '0d'          → maxDate 'today' (bootstrap-datepicker parity, e.g. birthday)
//   data-min-date      d.m.Y         → minDate
//   data-max-date      'today'|d.m.Y → maxDate (e.g. order-history range: no future dates)
//
// IMPORTANT — admin card forms and many frontend forms are injected via jQuery AJAX AFTER
// DOMContentLoaded, so init runs on DOMContentLoaded + jQuery ajaxComplete + focus. Init is
// idempotent (flatpickr sets el._flatpickr).
//
// IMPORTANT — the range value MUST stay "DD.MM.YYYY - DD.MM.YYYY": the document-archive
// backend splits on "-". The German locale would otherwise join with " bis ", so we force
// rangeSeparator: ' - '.

import flatpickr from 'flatpickr';
import { German } from 'flatpickr/dist/l10n/de.js';
import 'flatpickr/dist/flatpickr.css';
// Must load after flatpickr.css — neutralises global input/select styling that breaks the header.
import '../css/flatpickr-overrides.css';

const DATE_SELECTOR = '.datepicker';
const DATETIME_SELECTOR = '.js-flatpickr-datetime';
const RANGE_SELECTOR = '.js-flatpickr-range';
const ALL_SELECTOR = `${DATE_SELECTOR}, ${DATETIME_SELECTOR}, ${RANGE_SELECTOR}`;

function baseOptions(el) {
    const lang = (el.dataset.language || '').toLowerCase().slice(0, 2);
    const opts = {
        // German unless the field is explicitly flagged English; frontend fields were
        // always German, so an absent data-language stays German.
        locale: lang === 'en' ? 'default' : German,
    };
    if (el.dataset.dateEndDate === '0d') opts.maxDate = 'today';
    if (el.dataset.minDate) opts.minDate = el.dataset.minDate;
    if (el.dataset.maxDate) opts.maxDate = el.dataset.maxDate;
    return opts;
}

function optionsFor(el) {
    const base = baseOptions(el);
    if (el.matches(DATETIME_SELECTOR)) {
        return { ...base, allowInput: true, enableTime: true, time_24hr: true, dateFormat: 'd.m.Y H:i' };
    }
    if (el.matches(RANGE_SELECTOR)) {
        // Two months side-by-side on desktop (like the old daterangepicker); one on narrow screens.
        const wide = typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 768px)').matches;
        // flatpickr reads the range separator from the locale (l10n.rangeSeparator), NOT from a
        // top-level option — override it on a locale copy. This keeps the submitted value
        // "DD.MM.YYYY - DD.MM.YYYY" (the document-archive backend splits on "-") and lets the
        // pre-filled value parse; the German locale would otherwise emit/expect " bis ".
        const localeBase = base.locale === German ? German : flatpickr.l10ns.default;
        const opts = {
            ...base,
            locale: { ...localeBase, rangeSeparator: ' - ' },
            mode: 'range',
            dateFormat: 'd.m.Y',
            showMonths: wide ? 2 : 1,
        };
        // With two panels, open on [previous month][current month] so the current month sits
        // in the RIGHT panel. Document/order-history search looks backwards, and the future
        // month is max-capped at today anyway — the previous month is the more useful view.
        if (wide) opts.onReady = (selectedDates, dateStr, fp) => fp.changeMonth(-1);
        return opts;
    }
    return { ...base, allowInput: true, dateFormat: 'd.m.Y' };
}

function initFlatpickr() {
    document.querySelectorAll(ALL_SELECTOR).forEach((el) => {
        if (!el._flatpickr) flatpickr(el, optionsFor(el));
    });
}

// Manual re-scan hook for legacy code that injects markup outside the triggers below.
window.initFlatpickr = initFlatpickr;

// 1) Server-rendered fields on first load.
document.addEventListener('DOMContentLoaded', initFlatpickr);

// 2) AJAX-injected forms (admin overlay, frontend partials).
if (window.jQuery) {
    window.jQuery(document).ajaxComplete(initFlatpickr);
}

// 3) Safety net: lazily initialise (and open) on first focus.
document.addEventListener('focusin', (e) => {
    const el = e.target;
    if (el && el.matches && el.matches(ALL_SELECTOR) && !el._flatpickr) {
        flatpickr(el, optionsFor(el)).open();
    }
});

// Inline event calendar for the collection "calendar" view (collection view type 2, rendered
// by module/collection/show_datepicker.inc.php). Replaces the old jQuery-UI inline datepicker.
// `events` is an array of [startDate, endDate, tooltipHtml] (Date objects). Days inside any
// range get a highlight class; where Bootstrap's tooltip plugin is present (it is in every shop
// bundle) they also get a click-triggered HTML tooltip, otherwise the stripped text as title.
window.initCollectionCalendar = function (selector, events) {
    const host = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!host || host._collectionCalendar) return;
    host._collectionCalendar = true;
    const list = Array.isArray(events) ? events : [];
    const $ = window.jQuery;
    // flatpickr drives an <input>; render the calendar inline inside the host div and hide the
    // input, so it stays a display-only calendar like the old jQuery-UI inline widget.
    const input = document.createElement('input');
    input.type = 'text';
    input.style.display = 'none';
    host.appendChild(input);
    flatpickr(input, {
        inline: true,
        locale: German,
        appendTo: host,
        onDayCreate(dObj, dStr, fp, dayElem) {
            const d = dayElem.dateObj;
            for (let i = 0; i < list.length; i++) {
                if (list[i][0] <= d && d <= list[i][1]) {
                    dayElem.classList.add('has-collection-event');
                    const html = list[i][2] || '';
                    if ($ && typeof $.fn.tooltip === 'function') {
                        dayElem.setAttribute('title', html);
                        $(dayElem).tooltip({ html: true, container: 'body', trigger: 'click' });
                    } else {
                        dayElem.setAttribute('title', html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim());
                    }
                    break;
                }
            }
        },
    });
};
